import { Head, Link } from '@inertiajs/react';

type CatalogPlan = {
    key: string;
    label: string;
    amount: number | null;
    period: string;
    badge: string | null;
    description: string | null;
    features: string[];
    highlighted: boolean;
    is_payable: boolean;
    is_trial: boolean;
    trial_days: number | null;
};

const pillars = [
    { icon: 'cloud_sync', title: 'Instant Multi-Branch Cloud Sync', body: 'Real-time sync across counters, warehouses, and branches with secure cloud backups.' },
    { icon: 'flash_on', title: 'Instant 3-Second Counter POS', body: 'Keyboard shortcuts, barcode auto-increment, multi-tender payments, and custom prefixes.' },
    { icon: 'gavel', title: 'Automated GSTIN & NIC Compliance', body: 'Direct NIC API for IRN, QR, and GSTR-1/2/3B/9 filing reports.' },
    { icon: 'inventory_2', title: 'Smart Inventory & BOM', body: 'Batch tracking, expiry alerts, manufacturing kits, and barcode labels from PO receipts.' },
    { icon: 'translate', title: 'Pan-India Multilingual WhatsApp', body: 'Invoices and reminders in Hindi, Gujarati, Marathi, Tamil, and more — with UPI QR.' },
    { icon: 'admin_panel_settings', title: 'Role-Based Security Matrix', body: 'Cashiers, warehouse, and owners get the right access with a tamper-aware audit trail.' },
];

export default function Landing({
    catalog = [],
    trialDays,
    downloads = {},
}: {
    catalog?: CatalogPlan[];
    trialDays: number;
    downloads?: {
        windows?: string | null;
        mac?: string | null;
        ios?: string | null;
        android?: string | null;
    };
}) {
    const platforms = [
        {
            key: 'windows' as const,
            title: 'Windows',
            body: 'Installable desktop app for Windows 10/11. Opens your Dums Suite cloud account in a native window.',
            icon: 'desktop_windows',
            url: downloads.windows,
            cta: 'Download for Windows',
        },
        {
            key: 'mac' as const,
            title: 'macOS',
            body: 'Native Mac app (.dmg). Same full web features with tray shortcuts for POS.',
            icon: 'laptop_mac',
            url: downloads.mac,
            cta: 'Download for Mac',
        },
        {
            key: 'ios' as const,
            title: 'iPhone & iPad',
            body: 'Run Dums Suite on iOS. Sign in with your company account to bill and check stock on the go.',
            icon: 'phone_iphone',
            url: downloads.ios,
            cta: downloads.ios ? 'Get on the App Store' : 'Coming soon on iOS',
        },
    ];

    return (
        <div className="landing-root min-h-screen bg-[#f7f9fb] text-[#191c1e]" style={{ fontFamily: 'Manrope, system-ui, sans-serif' }}>
            <Head title="Pan-India Cloud GST Billing & ERP">
                <link
                    href="https://fonts.googleapis.com/css2?family=Manrope:wght@400;500;600;700&family=JetBrains+Mono:wght@500;700&display=swap"
                    rel="stylesheet"
                />
                <link
                    href="https://fonts.googleapis.com/css2?family=Material+Symbols+Outlined:opsz,wght,FILL,GRAD@20..48,100..700,0..1,-50..200"
                    rel="stylesheet"
                />
                <style>{`
                    .landing-root { scroll-behavior: smooth; }
                    .material-symbols-outlined { font-variation-settings: 'FILL' 0, 'wght' 500, 'GRAD' 0, 'opsz' 24; vertical-align: middle; }
                    @keyframes rise { from { opacity: 0; transform: translateY(18px); } to { opacity: 1; transform: translateY(0); } }
                    @keyframes floaty { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-8px); } }
                    .rise { animation: rise .7s ease-out both; }
                    .rise-d1 { animation-delay: .12s; }
                    .rise-d2 { animation-delay: .24s; }
                    .rise-d3 { animation-delay: .36s; }
                    .hero-preview img { backface-visibility: hidden; }
                `}</style>
            </Head>

            <header className="fixed top-0 z-50 w-full border-b border-[#bcc9c6]/40 bg-[#f7f9fb]/90 backdrop-blur-xl">
                <div className="mx-auto flex h-20 max-w-7xl items-center justify-between gap-4 px-6 lg:px-12">
                    <Link href="/" className="flex shrink-0 items-center">
                        <img
                            src="/brand/dums-suite-wordmark.png?v=3"
                            alt="DUMS Suite"
                            width={200}
                            height={58}
                            className="h-11 w-auto object-contain sm:h-12"
                        />
                    </Link>
                    <nav className="hidden items-center gap-7 xl:flex">
                        <a href="#features" className="text-sm text-[#3d4947] hover:text-[#191c1e]">Features</a>
                        <a href="#hardware" className="text-sm text-[#3d4947] hover:text-[#191c1e]">Hardware & POS</a>
                        <a href="#download" className="text-sm text-[#3d4947] hover:text-[#191c1e]">Download</a>
                        <a href="#pricing" className="text-sm text-[#3d4947] hover:text-[#191c1e]">Pricing</a>
                    </nav>
                    <div className="flex items-center gap-3">
                        <Link
                            href={route('login')}
                            className="hidden rounded-lg bg-[#e6e8ea] px-4 py-2 text-[13px] font-semibold text-[#191c1e] hover:bg-[#e0e3e5] md:inline-flex"
                        >
                            Book Live Demo
                        </Link>
                        <Link
                            href={route('register')}
                            className="inline-flex rounded-lg bg-[#00685f] px-5 py-2.5 text-[13px] font-semibold text-white shadow-[0_2px_8px_rgba(0,104,95,0.25)] hover:bg-[#008378]"
                        >
                            Start Free {trialDays}-Day Trial
                        </Link>
                        <Link
                            href={route('login')}
                            className="flex h-8 w-8 items-center justify-center rounded-full bg-[#00685f] text-white"
                            aria-label="Log in"
                        >
                            <span className="material-symbols-outlined text-[18px]">person</span>
                        </Link>
                    </div>
                </div>
            </header>

            <main className="pt-20">
                <section className="relative overflow-hidden px-6 pb-20 pt-12 lg:px-12">
                    <div className="pointer-events-none absolute -left-32 -top-32 h-96 w-96 rounded-full bg-[#00685f]/5 blur-3xl" />
                    <div className="relative z-10 mx-auto max-w-7xl">
                        <div className="mx-auto flex max-w-4xl flex-col items-center text-center">
                            <div className="mb-6 inline-flex items-center gap-2 rounded-full bg-[#e6e8ea] px-4 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-[#3d4947] shadow-sm">
                                <span className="material-symbols-outlined text-[18px] text-[#00685f]">cloud_done</span>
                                GST Compliant · 100% Cloud-Native · Real-Time Multi-Branch
                            </div>
                            <h1 className="rise rise-d1 text-3xl font-bold tracking-tight text-[#191c1e] sm:text-4xl">
                                High-Velocity GST Billing & Multi-Branch Cloud ERP That Never Stops
                            </h1>
                            <p className="rise rise-d2 mt-6 max-w-3xl text-base leading-relaxed text-[#3d4947] sm:text-lg">
                                Power retail counters, wholesale distribution, and multi-branch enterprise with sub-3-second cloud billing,
                                real-time sync, automated GSTR filing, and WhatsApp invoices in Indian languages.
                            </p>
                            <div className="rise rise-d3 mt-8 flex w-full max-w-xl flex-col items-center justify-center gap-4 sm:flex-row">
                                <Link
                                    href={route('register')}
                                    className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-[#00685f] px-8 py-4 text-base font-semibold text-white shadow-lg shadow-[#00685f]/25 hover:bg-[#008378] sm:w-auto"
                                >
                                    <span className="material-symbols-outlined text-[20px]">bolt</span>
                                    Start {trialDays}-Day Free Trial
                                </Link>
                                <a
                                    href="#hardware"
                                    className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-[#e6e8ea] px-7 py-4 text-base font-semibold text-[#191c1e] hover:bg-[#e0e3e5] sm:w-auto"
                                >
                                    <span className="material-symbols-outlined text-[20px] text-[#00685f]">calendar_today</span>
                                    Schedule Hardware Demo
                                </a>
                            </div>
                            <p className="mt-3 font-mono text-[11px] text-[#3d4947]">
                                Instant cloud activation · Zero credit card · 10+ Indian languages
                            </p>
                        </div>

                        <div className="hero-preview mt-14 w-full overflow-hidden rounded-2xl border border-[#e0e3e5] bg-white shadow-2xl">
                            <div className="flex items-center justify-between border-b border-[#e0e3e5] bg-[#e6e8ea] px-4 py-3">
                                <div className="flex items-center gap-2">
                                    <div className="flex gap-1.5">
                                        <div className="h-3 w-3 rounded-full bg-[#ba1a1a]/80" />
                                        <div className="h-3 w-3 rounded-full bg-[#f59e0b]/80" />
                                        <div className="h-3 w-3 rounded-full bg-[#4edea3]" />
                                    </div>
                                    <div className="ml-4 hidden items-center gap-2 rounded-md bg-[#eceef0] px-3 py-1 font-mono text-[11px] text-[#3d4947] sm:flex">
                                        <span className="material-symbols-outlined text-[14px] text-[#00685f]">lock</span>
                                        suite.dumstech.in
                                    </div>
                                </div>
                                <span className="hidden items-center gap-1.5 rounded-full bg-[#006947]/10 px-2.5 py-0.5 font-mono text-[11px] font-semibold text-[#006947] md:inline-flex">
                                    <span className="h-2 w-2 animate-pulse rounded-full bg-[#006947]" />
                                    LIVE CLUSTER
                                </span>
                            </div>
                            <img
                                src="/landing/assets/hero-cockpit.png?v=20261002"
                                alt="DUMS Suite billing cockpit preview"
                                width={1024}
                                height={519}
                                decoding="async"
                                className="block h-auto w-full object-contain"
                            />
                        </div>
                    </div>
                </section>

                <section id="features" className="bg-[#f2f4f6] px-6 py-20 lg:px-12">
                    <div className="mx-auto max-w-7xl">
                        <div className="mb-14 flex flex-col justify-between gap-6 md:flex-row md:items-end">
                            <div>
                                <span className="font-mono text-[11px] font-bold uppercase tracking-wider text-[#00685f]">Enterprise Architecture</span>
                                <h2 className="mt-2 text-3xl font-bold tracking-tight">Engineered for Counter Speed & Statutory Rigor</h2>
                            </div>
                            <p className="max-w-md text-sm text-[#3d4947]">
                                Six core modules for Indian manufacturers, wholesalers, and retail chains.
                            </p>
                        </div>
                        <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
                            {pillars.map((p) => (
                                <div key={p.title} className="flex flex-col rounded-2xl bg-white p-6 shadow-sm">
                                    <div className="mb-5 flex h-12 w-12 items-center justify-center rounded-xl bg-[#89f5e7] text-[#00201d]">
                                        <span className="material-symbols-outlined text-[26px]">{p.icon}</span>
                                    </div>
                                    <h3 className="mb-2 text-lg font-semibold">{p.title}</h3>
                                    <p className="text-sm leading-relaxed text-[#3d4947]">{p.body}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                <section id="hardware" className="bg-[#f2f4f6] px-6 py-20 lg:px-12">
                    <div className="mx-auto max-w-7xl">
                        <span className="font-mono text-[11px] font-bold uppercase tracking-wider text-[#00685f]">Zero Driver Friction</span>
                        <h2 className="mt-2 text-3xl font-bold tracking-tight">Seamless Plug-and-Play Hardware</h2>
                        <div className="mt-10 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
                            {[
                                ['print', 'ESC/POS Thermal'],
                                ['barcode_scanner', '1D/2D Barcode'],
                                ['scale', 'Weighing Scales'],
                                ['desktop_windows', 'Customer Display'],
                                ['point_of_sale', 'Cash Drawers'],
                            ].map(([icon, label]) => (
                                <div key={label} className="flex flex-col items-center rounded-2xl bg-white p-5 text-center shadow-sm">
                                    <div className="mb-3 flex h-14 w-14 items-center justify-center rounded-xl bg-[#e6e8ea] text-[#00685f]">
                                        <span className="material-symbols-outlined text-[30px]">{icon}</span>
                                    </div>
                                    <span className="text-sm font-semibold">{label}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                <section id="download" className="bg-[#f7f9fb] px-6 py-20 lg:px-12">
                    <div className="mx-auto max-w-7xl">
                        <div className="mx-auto mb-12 max-w-3xl text-center">
                            <span className="font-mono text-[11px] font-bold uppercase tracking-wider text-[#00685f]">Get the apps</span>
                            <h2 className="mt-2 text-3xl font-bold tracking-tight">Download for desktop &amp; iOS</h2>
                            <p className="mt-3 text-sm text-[#3d4947]">
                                Use the same cloud account in a dedicated Windows or Mac app, or on iPhone / iPad. Web browser access stays available anytime.
                            </p>
                        </div>
                        <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
                            {platforms.map((p) => {
                                const ready = Boolean(p.url);
                                return (
                                    <div key={p.key} className="flex flex-col rounded-2xl border border-[#e0e3e5] bg-white p-6 shadow-sm">
                                        <div className="mb-5 flex h-12 w-12 items-center justify-center rounded-xl bg-[#89f5e7] text-[#00201d]">
                                            <span className="material-symbols-outlined text-[26px]">{p.icon}</span>
                                        </div>
                                        <h3 className="mb-2 text-lg font-semibold">{p.title}</h3>
                                        <p className="mb-6 flex-1 text-sm leading-relaxed text-[#3d4947]">{p.body}</p>
                                        {ready ? (
                                            <a
                                                href={p.url!}
                                                className="inline-flex items-center justify-center gap-2 rounded-xl bg-[#00685f] px-5 py-3 text-sm font-semibold text-white hover:bg-[#008378]"
                                                {...(p.url!.startsWith('http') ? { target: '_blank', rel: 'noopener noreferrer' } : { download: true })}
                                            >
                                                <span className="material-symbols-outlined text-[18px]">download</span>
                                                {p.cta}
                                            </a>
                                        ) : (
                                            <span className="inline-flex cursor-not-allowed items-center justify-center gap-2 rounded-xl bg-[#e6e8ea] px-5 py-3 text-sm font-semibold text-[#3d4947]">
                                                Coming soon
                                            </span>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                        <p className="mt-8 text-center font-mono text-[11px] text-[#3d4947]">
                            Prefer the browser? <Link href={route('login')} className="font-semibold text-[#00685f] hover:underline">Log in on the web</Link>
                            {' · '}
                            <Link href={route('register')} className="font-semibold text-[#00685f] hover:underline">Start free trial</Link>
                        </p>
                    </div>
                </section>

                <section id="pricing" className="bg-[#f7f9fb] px-6 py-20 lg:px-12">
                    <div className="mx-auto max-w-7xl">
                        <div className="mx-auto mb-14 max-w-3xl text-center">
                            <span className="font-mono text-[11px] font-bold uppercase tracking-wider text-[#00685f]">Predictable Investment</span>
                            <h2 className="mt-2 text-3xl font-bold tracking-tight">Clear Plans for Growing Indian Businesses</h2>
                        </div>
                        <div className={`grid grid-cols-1 items-stretch gap-8 ${catalog.length > 3 ? 'md:grid-cols-2 xl:grid-cols-4' : 'md:grid-cols-3'}`}>
                            {catalog.map((plan) => (
                                <PricingCard
                                    key={plan.key}
                                    name={plan.label}
                                    price={plan.is_trial ? 0 : plan.amount}
                                    period={plan.is_trial ? `${plan.trial_days || trialDays} days` : (plan.amount == null ? null : 'billed yearly')}
                                    highlighted={plan.highlighted}
                                    badge={plan.badge || undefined}
                                    cta={
                                        plan.is_trial
                                            ? `Start ${plan.trial_days || trialDays}-Day Free Trial`
                                            : plan.is_payable
                                                ? `Choose ${plan.label}`
                                                : 'Request Enterprise Proposal'
                                    }
                                    features={plan.features?.length ? plan.features : ['Full Cloud POS access']}
                                />
                            ))}
                        </div>
                    </div>
                </section>

                <section className="px-6 py-20 lg:px-12">
                    <div className="relative mx-auto max-w-7xl overflow-hidden rounded-3xl bg-[#2d3133] p-8 text-[#eff1f3] shadow-2xl sm:p-12 lg:p-16">
                        <div className="pointer-events-none absolute -bottom-20 -right-20 h-80 w-80 rounded-full bg-[#00685f]/20 blur-3xl" />
                        <div className="relative z-10 grid grid-cols-1 items-center gap-10 lg:grid-cols-12">
                            <div className="lg:col-span-7">
                                <h2 className="text-3xl font-bold tracking-tight text-white">Ready to Accelerate Your Billing Counter?</h2>
                                <p className="mt-4 max-w-xl text-base text-[#bcc9c6]">
                                    Join forward-thinking Indian businesses. Get set up in under 15 minutes with instant cloud activation and live GST API bridge.
                                </p>
                            </div>
                            <div className="lg:col-span-5">
                                <Link
                                    href={route('register')}
                                    className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-[#00685f] px-6 py-4 text-sm font-semibold text-white hover:bg-[#008378]"
                                >
                                    <span className="material-symbols-outlined text-[20px]">bolt</span>
                                    Launch Free Trial
                                </Link>
                                <p className="mt-3 text-center font-mono text-[11px] text-[#bcc9c6]">
                                    Prefer phone help? Call <span className="font-bold text-[#89f5e7]">+91 7990452618</span>
                                </p>
                            </div>
                        </div>
                    </div>
                </section>
            </main>

            <footer className="border-t border-[#e0e3e5] bg-white px-6 py-10 lg:px-12">
                <div className="mx-auto flex max-w-7xl flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
                    <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-4">
                        <img
                            src="/brand/dums-suite-wordmark.png?v=3"
                            alt="DUMS Suite"
                            width={200}
                            height={58}
                            className="h-11 w-auto object-contain"
                        />
                        <p className="text-sm text-[#3d4947]">Cloud GST billing & ERP for Indian businesses.</p>
                    </div>
                    <div className="flex gap-4 text-sm">
                        <Link href={route('login')} className="font-semibold text-[#00685f] hover:underline">
                            Log in
                        </Link>
                        <Link href={route('register')} className="font-semibold text-[#00685f] hover:underline">
                            Register
                        </Link>
                        <Link href={route('terms.show')} className="text-[#3d4947] hover:underline">
                            Terms
                        </Link>
                    </div>
                </div>
            </footer>
        </div>
    );
}

function PricingCard({
    name,
    price,
    period,
    features,
    cta,
    highlighted,
    badge,
}: {
    name: string;
    price: number | null | undefined;
    period?: string | null;
    features: string[];
    cta: string;
    highlighted?: boolean;
    badge?: string;
}) {
    return (
        <div
            className={`relative flex flex-col justify-between rounded-2xl p-8 shadow-sm ${
                highlighted ? 'bg-[#2d3133] text-[#eff1f3] shadow-xl' : 'bg-white text-[#191c1e]'
            }`}
        >
            {badge ? (
                <div className="absolute -top-3.5 right-6 rounded-full bg-[#00685f] px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-white shadow-md">
                    {badge}
                </div>
            ) : null}
            <div>
                <span className={`text-lg font-semibold ${highlighted ? 'text-white' : ''}`}>{name}</span>
                <div className="mt-6 flex items-baseline gap-1">
                    <span className={`text-3xl font-bold ${highlighted ? 'text-white' : ''}`}>
                        {price == null ? 'Custom Quote' : price === 0 ? 'Free' : `₹${price.toLocaleString('en-IN')}`}
                    </span>
                    {period ? (
                        <span className={`text-sm ${highlighted ? 'text-[#bcc9c6]' : 'text-[#3d4947]'}`}>/ {period}</span>
                    ) : null}
                </div>
                <ul className={`mt-8 flex flex-col gap-3 text-sm ${highlighted ? 'text-white' : 'text-[#3d4947]'}`}>
                    {features.map((f) => (
                        <li key={f} className="flex items-center gap-2.5">
                            <span className={`material-symbols-outlined text-[18px] ${highlighted ? 'text-[#4edea3]' : 'text-[#00685f]'}`}>
                                check
                            </span>
                            {f}
                        </li>
                    ))}
                </ul>
            </div>
            <div className="mt-8 pt-6">
                <Link
                    href={route('register')}
                    className={`flex w-full items-center justify-center rounded-xl py-3 text-sm font-semibold transition ${
                        highlighted
                            ? 'bg-[#00685f] text-white hover:bg-[#008378]'
                            : 'bg-[#e6e8ea] text-[#191c1e] hover:bg-[#e0e3e5]'
                    }`}
                >
                    {cta}
                </Link>
            </div>
        </div>
    );
}
